import type { ReactNode } from "react";
import { Check, Image, TriangleAlert } from "lucide-react";
import type { FileResponse, ReadStatus } from "@patent-studio/api-types";
import { HoverTip } from "@/components/ui/HoverTip";
import { Spinner } from "@/components/ui/Spinner";
import { StatusPill, type PillTone } from "@/components/ui/StatusPill";
import { agree, listed } from "@/lib/plural";

/** What a file's chip is drawn from: the server's status, the pages it could not read, and why it was not read. */
export interface FileRead {
  status: ReadStatus;
  unreadPages: readonly number[] | null;
  /** Why the file was not read, in the attorney's words, where the caller knows. */
  reason: string | null;
}

const WORDS: Record<
  ReadStatus,
  { words: string; tone: PillTone; icon: ReactNode; tip: string }
> = {
  reading: {
    words: "Reading…",
    tone: "brand",
    icon: <Spinner size="sm" />,
    tip: "Patent Studio is reading this file.",
  },
  read: {
    words: "Read",
    tone: "success",
    icon: <Check aria-hidden />,
    tip: "Patent Studio has read the full text of this file.",
  },
  read_ocr: {
    words: "Read (OCR)",
    tone: "success",
    icon: <Check aria-hidden />,
    tip: "Scanned document. Patent Studio read the text with OCR (optical character recognition). Check key passages against the original.",
  },
  partly_readable: {
    words: "Partly readable",
    tone: "warning",
    icon: <TriangleAlert aria-hidden />,
    tip: "Patent Studio could only read part of this file.",
  },
  not_read: {
    words: "Not read – stored only",
    tone: "warning",
    icon: <TriangleAlert aria-hidden />,
    tip: "Patent Studio has stored this file but has not read it.",
  },
  drawing: {
    words: "Drawing",
    tone: "neutral",
    icon: <Image aria-hidden />,
    tip: "Used as a drawing in Figures. Its text isn't read.",
  },
};

/**
 * Why a read failed, in the attorney's words. The worker's stored refusal is not shown: some
 * refusals name the stored object or the reading service's job, which are ours, not theirs.
 */
const COULD_NOT_READ =
  "Patent Studio could not read this file. Upload it again, or upload a text-based copy.";

/** A served file's read, or a stage source's, as its chip draws it. */
export function readOf(
  file: Pick<FileResponse, "read_status" | "unread_pages">,
): FileRead {
  const reason = file.read_status === "not_read" ? COULD_NOT_READ : null;
  return { status: file.read_status, unreadPages: file.unread_pages, reason };
}

/** Whether a file's row is marked for the attorney: some or all of its text was not read. */
export function needsAttention(status: ReadStatus): boolean {
  return status === "partly_readable" || status === "not_read";
}

/** The pages as runs, "3, 24 to 26 and 31", in the order given. */
function pageRuns(pages: readonly number[]): string {
  const runs: [number, number][] = [];
  for (const page of pages) {
    const last = runs.at(-1);
    if (last !== undefined && page === last[1] + 1) last[1] = page;
    else runs.push([page, page]);
  }
  return listed(
    runs.map(([first, end]) =>
      first === end ? String(first) : `${String(first)} to ${String(end)}`,
    ),
  );
}

/** What the tip says beyond the status: which pages, or why, and what to do. */
function detail({ status, unreadPages, reason }: FileRead): string | null {
  if (status === "partly_readable" && unreadPages && unreadPages.length > 0) {
    return `${agree(unreadPages.length, "Page", "Pages")} ${pageRuns(unreadPages)} could not be read. Upload a clearer scan or a text-based PDF.`;
  }
  if (status === "not_read") return reason;
  return null;
}

/**
 * Whether Praxis read a file, and how well, as one chip with the reason on hover or focus.
 *
 * The same chip on every list of files, so an attorney reads a file's status the same way on
 * Upload Materials, in a stage's sources and on the prior art list.
 */
export function ReadStatusChip({ read }: { read: FileRead }) {
  const { words, tone, icon, tip } = WORDS[read.status];
  const more = detail(read);
  return (
    <HoverTip
      tip={more === null ? tip : `${tip} ${more}`}
      wide={false}
      align="end"
    >
      {({ triggerProps }) => (
        <StatusPill
          tone={tone}
          size="chip"
          plain
          icon={icon}
          tabIndex={0}
          data-testid="read-status"
          data-read-status={read.status}
          {...triggerProps}
        >
          {words}
        </StatusPill>
      )}
    </HoverTip>
  );
}
